import { Box, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LinkButton } from "~/common/components/Link";
import Spinner from "~/common/components/Spinner/Spinner";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import type { RootState } from "~/app/store";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import type { MultipleEntriesRow } from "./addToCartResultUtils";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const ContentGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "auto",
  gridTemplateAreas: `
    "toggle toggle"
    "table table"
  `,
});

const StyledTable = styled(Table)(({ theme }) => ({
  gridArea: "table",
  "& tbody tr td": {
    verticalAlign: "middle",
  },
  [theme.breakpoints.up("tablet")]: {
    "& thead tr th:first-child": {
      width: "30%",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    "& thead tr th:first-child": {
      width: "unset",
    },
  },
}));

const EntryCell = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

interface ResolvedEntry {
  productCode: string;
  designation: string;
  quantity: number;
  saleNetPrice?: number;
}

export interface MultipleItemsViewProps {
  entries: ResolvedEntry[];
  isTableExpanded: boolean;
  onToggle: () => void;
  showTableLabel: string;
  hideTableLabel: string;
  invoiceProductLabel: string;
  quantityLabel: string;
}

export const MultipleItemsView = ({
  entries,
  isTableExpanded,
  onToggle,
  showTableLabel,
  hideTableLabel,
  invoiceProductLabel,
  quantityLabel,
}: MultipleItemsViewProps) => {
  return (
    <ContentGrid>
      <Box sx={{ gridArea: "toggle" }}>
        <LinkButton
          iconBefore={isTableExpanded ? <KeyboardArrowUpIcon  /> : <KeyboardArrowDownIcon  />}
          onClick={onToggle}
        >
          {isTableExpanded ? hideTableLabel : showTableLabel}
        </LinkButton>
      </Box>
      {isTableExpanded && (
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>{invoiceProductLabel}</TableCell>
              <TableCell>{quantityLabel}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.productCode}>
                <TableCell>
                  <EntryCell>
                    <Typography component="p" sx={{ fontWeight: "bold", margin: 0, marginRight: 0.5 }}>
                      {entry.designation}
                    </Typography>
                    {entry.saleNetPrice && <SaleTag size={16} />}
                  </EntryCell>
                </TableCell>
                <TableCell>
                  <Typography component="p" sx={{ margin: 0 }}>
                    {entry.quantity}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      )}
    </ContentGrid>
  );
};

const AddToCartResultsMultipleItems = ({ entries }: { entries: MultipleEntriesRow[] }) => {
  const { t } = useTranslation();
  const { val: table, setTrue: open, setFalse: close } = useBooleanState(true);

  const getProductsBasicInfo = (state: RootState) => state.products.basic;
  const productsBasicInfo = useSelector(getProductsBasicInfo);

  if (!entries) {
    return <Spinner />;
  }

  const resolvedEntries: ResolvedEntry[] = entries.map((entry) => ({
    productCode: entry.productCode,
    designation: entry.designation ?? productsBasicInfo[entry.productCode]?.designation ?? entry.productCode,
    quantity: entry.quantity,
    saleNetPrice: entry.saleNetPrice,
  }));

  return (
    <MultipleItemsView
      entries={resolvedEntries}
      isTableExpanded={table}
      onToggle={() => (table ? close() : open())}
      showTableLabel={t("addToCart.showTable")}
      hideTableLabel={t("addToCart.hideTable")}
      invoiceProductLabel={t("feedback.option.invoiceProduct")}
      quantityLabel={t("input.quantity")}
    />
  );
};

export default AddToCartResultsMultipleItems;
